import InputUI from "@UI/InputUI";
import { changeValByPath } from "@utils";
import { computed } from "mobx";
import { inject, observer } from "mobx-react";
import { Card } from "primereact/card";
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import ButtonUI from "@UI/ButtonUI";
import DragRow from "./DragRow";
import SortableAddressItem from "./SortableAddressItem";

// dnd-kit
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";

import "./style.scss";

const data = [];

const FilialSideBar = inject("mainStore")(
    observer(({ mainStore, setVisible = () => {}, regionIds, cityIds }) => {
        const cityIds_suggest = computed(() => mainStore.getMainStore("suggest.cityIds_suggest")).get();
        const detailData = computed(() => mainStore.getMainStore("detailData")).get();

        const [filial, setFilial] = useState(null);
        const [selectedAddresses, setSelectedAddresses] = useState([]);
        const [disabledSave, setDisabledSave] = useState(true);

        const { secondParam } = useParams();
        const regionCity = data.filter((f) => regionIds?.find((s) => s === f.id));

        const city =
            cityIds?.reduce(
                (acc, row) => {
                    const find = cityIds_suggest.find((f) => f._id == row);
                    if (find) acc.push(find);
                    return acc;
                },
                [...regionCity],
            ) || [];

        useEffect(() => {
            if (city.length && !filial) getFilial();
        }, [city.length, detailData?.addressId]);

        useEffect(() => {
            const val = validKeys(selectedAddresses) || !filial?.title;
            setDisabledSave(val);
        }, [selectedAddresses, filial?.title]);

        const getFilial = async () => {
            let data = {
                title: "",
                addresses: [],
                offerId: secondParam !== "add" ? secondParam : null,
            };

            if (detailData?.addressId) {
                const resp = await mainStore.getByUrl(`promo/offeraddresses/${detailData.addressId}`);
                if (resp && resp.success) {
                    data = { ...resp.data };
                }
            }

            mainStore.setMainStore("filial", data);

            if (!data.addresses.length) {
                const arr = city.map((i) => ({
                    city: i.city,
                    address: [{ id: generateId(), text: "" }],
                    isSelect: true,
                }));
                data.addresses = arr;
            } else {
                const filteredAddress = data.addresses
                    .filter((f) => city.find((find) => find.city == f.city))
                    .map((m) => ({
                        ...m,
                        address: m.address.map((addr) => ({
                            ...addr,
                            id: addr.id || generateId(),
                        })),
                        isSelect: "isSelect" in m ? m.isSelect : true,
                    }));

                if (!filteredAddress.length && city.length) {
                    data.addresses = city.map((i) => ({
                        city: i.city,
                        address: [{ id: generateId(), text: "" }],
                        isSelect: true,
                    }));
                } else {
                    const allowedCities = new Set(filteredAddress.map((item) => item.city));
                    const filtered = city.filter((item) => !allowedCities.has(item.city));
                    const arrNew = filtered.map((i) => ({
                        city: i.city,
                        address: [{ id: generateId(), text: "" }],
                        isSelect: true,
                    }));

                    data.addresses = filteredAddress.concat(arrNew);
                    setFilial(data);
                    setSelectedAddresses(data.addresses);
                    return;
                }
            }

            setSelectedAddresses(data.addresses);
            setFilial(data);
        };

        const handleSaveAddress = async () => {
            const payload = {
                ...filial,
                addresses: selectedAddresses,
            };
            if (filial._id) {
                await mainStore.putByUrl(`promo/offeraddresses/${filial._id}`, payload, "filial");
            } else {
                const req = await mainStore.postByUrl("promo/offeraddresses", payload, "filial");
                if (req.success) mainStore.setMainStore("detailData.addressId", req.data._id);
            }
            setVisible(false);
        };

        // --- dnd-kit: обработка сортировки строк (городов) ---
        const rowSensors = useSensors(
            useSensor(PointerSensor, {
                activationConstraint: {
                    distance: 5,
                },
            }),
        );

        const handleRowDragEnd = useCallback(
            (event) => {
                const { active, over } = event;
                if (!over || active.id === over.id) return;

                const oldIndex = selectedAddresses.findIndex((item) => item.city === active.id);
                const newIndex = selectedAddresses.findIndex((item) => item.city === over.id);

                if (oldIndex === -1 || newIndex === -1) return;
                const updated = arrayMove(selectedAddresses, oldIndex, newIndex);
                const newFilial = changeValByPath("addresses", updated, filial);
                setFilial(newFilial);
                setSelectedAddresses(updated);
            },
            [selectedAddresses],
        );

        // --- dnd-kit: обработка сортировки адресов внутри строки ---
        const handleAddressDragEnd = useCallback(
            (event, rowIndex) => {
                const { active, over } = event;
                if (!over || active.id === over.id) return;

                const currentRow = selectedAddresses[rowIndex];
                const addresses = [...currentRow.address];

                const oldIndex = addresses.findIndex((item) => item.id === active.id);
                const newIndex = addresses.findIndex((item) => item.id === over.id);

                if (oldIndex === -1 || newIndex === -1) return;

                const newAddresses = arrayMove(addresses, oldIndex, newIndex);

                const updatedRow = { ...currentRow, address: newAddresses };
                const updatedRows = [...selectedAddresses];
                updatedRows[rowIndex] = updatedRow;

                const newFilial = changeValByPath("addresses", updatedRows, filial);
                setFilial(newFilial);
                setSelectedAddresses(updatedRows);
            },
            [selectedAddresses],
        );

        // Генерация ID для адресов
        const generateId = () => {
            return `addr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        };

        // --- Остальная логика ---
        const updateFilialAndSelection = (path, newValue, rowIndex) => {
            const newFilial = changeValByPath(path, newValue, filial);
            setFilial(newFilial);

            const updatedRow = newFilial.addresses[rowIndex];
            const isSelected = selectedAddresses.some((s) => s.city === updatedRow.city);

            if (isSelected) {
                setSelectedAddresses(selectedAddresses.map((s) => (s.city === updatedRow.city ? updatedRow : s)));
            }
        };

        const toggleSelect = (isSelect, el, rowIndex) => {
            const elem = { ...el, isSelect: isSelect };

            const newFilial = changeValByPath(`addresses.${rowIndex}`, elem, filial);
            setFilial(newFilial);

            setSelectedAddresses(newFilial.addresses);
        };

        // Обработчики для адресов
        const handleAddressTextChange = (rowIndex, addressIndex, value) => {
            const currentRow = { ...selectedAddresses[rowIndex] };
            const addresses = [...currentRow.address];
            addresses[addressIndex].text = value;

            updateFilialAndSelection(`addresses.${rowIndex}.address`, addresses, rowIndex);
        };

        const handleAddAddress = (rowIndex) => {
            const currentRow = { ...selectedAddresses[rowIndex] };
            const newAddresses = [...currentRow.address, { id: generateId(), text: "" }];

            updateFilialAndSelection(`addresses.${rowIndex}.address`, newAddresses, rowIndex);
        };

        const handleRemoveAddress = (rowIndex, addressIndex) => {
            const currentRow = { ...selectedAddresses[rowIndex] };
            const newAddresses = currentRow.address.filter((_, index) => index !== addressIndex);

            updateFilialAndSelection(`addresses.${rowIndex}.address`, newAddresses, rowIndex);
        };

        const addressSensors = useSensors(
            useSensor(PointerSensor, {
                activationConstraint: {
                    distance: 5,
                },
            }),
        );

        const renderAddressesBody = (item, options) => {
            const rowIndex = options.rowIndex;
            const addressIds = item.address.map((addr) => addr.id);

            // Сенсоры для адресов

            return (
                <div className="columns__block">
                    <div className="address-list">
                        <DndContext
                            sensors={addressSensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event) => handleAddressDragEnd(event, rowIndex)}
                            // Убрали модификаторы
                        >
                            <SortableContext items={addressIds} strategy={verticalListSortingStrategy}>
                                {item.address.map((addressItem, addressIndex) => (
                                    <SortableAddressItem
                                        key={addressItem.id}
                                        id={addressItem.id}
                                        index={addressIndex}
                                        address={addressItem}
                                        onTextChange={(value) => handleAddressTextChange(rowIndex, addressIndex, value)}
                                        onRemove={() => handleRemoveAddress(rowIndex, addressIndex)}
                                        showRemove={item.address.length > 1}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    </div>

                    <ButtonUI
                        label="Добавить адрес"
                        icon="pi pi-plus"
                        outlined
                        onClick={(e) => {
                            e.stopPropagation();
                            handleAddAddress(rowIndex);
                        }}
                    />
                </div>
            );
        };

        // IDs для SortableContext строк
        const itemsIds = selectedAddresses.map((item) => item.city);

        return (
            <Card className="filial-side-bar" title="Кастомное поле">
                <InputUI
                    label="Введите название кастомного поля, которое увидит пользователь перед выбором*"
                    require
                    value={filial?.title || ""}
                    style={{ flex: 1 }}
                    onChange={(e) => setFilial(changeValByPath("title", e.target.value, filial))}
                    className="filial-side-bar__text"
                    placeholder="Филиал / клиника / студия"
                />

                <div className="filial-side-bar__table">
                    <span>Привяжите адреса к ГЕО*</span>

                    {/* Заголовок */}
                    <div className="table-header">
                        <div className="table-cell checkbox-cell"></div>
                        <div className="table-cell city-cell">Города</div>
                        <div className="table-cell addresses-cell">Адреса</div>
                    </div>

                    {/* DnD контекст для строк */}
                    <DndContext sensors={rowSensors} collisionDetection={closestCenter} onDragEnd={handleRowDragEnd}>
                        <SortableContext items={itemsIds} strategy={verticalListSortingStrategy}>
                            <div className="table-body">
                                {selectedAddresses.length > 0 ? (
                                    selectedAddresses.map((row, idx) => <DragRow key={row.city} row={row} rowIndex={idx} isSelected={row.isSelect} onToggleSelect={(e) => toggleSelect(e, row, idx)} renderBody={renderAddressesBody} />)
                                ) : (
                                    <div className="p-datatable-emptymessage">Ничего не найдено</div>
                                )}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>

                <ButtonUI label={"Сохранить адреса"} severity="success" onClick={handleSaveAddress} disabled={disabledSave} />
            </Card>
        );
    }),
);

const validKeys = (arr) => {
    let isNotValid = true;

    for (let i = 0; i < arr?.length; i++) {
        const el = arr[i];
        const objValid = { text: "" };

        const validKey = Object.entries(objValid).some(([key, val]) => {
            if (Array.isArray(el.address)) {
                return validKeys(el.address);
            } else {
                return el[key] == val;
            }
        });

        isNotValid = validKey;
        if (validKey) break;
    }

    return isNotValid;
};

export default FilialSideBar;
